import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import { ThemeProps, css, withTheme } from "styled-components";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import React, { ChangeEvent, useEffect, useState } from "react";
import GridContainer, { GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Radio, { RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps, RadioGroupStyle } from "@insite/mobius/RadioGroup";
import TextField from "@insite/mobius/TextField";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { connect } from "react-redux";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FormFieldStyle } from "@insite/mobius/FormField";
import getColor from "@insite/mobius/utilities/getColor";
import CustomModelRecap from "./ADICustomModalRecap";
import styled from "styled-components";
import ADIColors from "@NA/Utils/ADIColors";
import { addBankAccount } from "@NA/Services/ADIInvoiceRecapBankAccountService";
import Icon from "@insite/mobius/Icon";
import ValidationSuccessIcon from "@NA/Icons/ValidationSuccessIcon";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ErrorCrossIcon from "@NA/Icons/ErrorCrossIcon";

export interface ADINewCreditCardModalRecapStyles {
    editInputWapper?: InjectableCss;
    newListContainer?: InjectableCss;
    cardTypeRadioGroup?: RadioGroupProps;
    errorGrid?: InjectableCss;
    expError?: InjectableCss;
    headingError?: InjectableCss;
    continueButton?: ButtonPresentationProps;
    labelText?: InjectableCss;
    buttonGridItem?: GridItemProps;
    primaryButton?: ButtonPresentationProps;
    secondaryButton?: ButtonPresentationProps;
    container?: InjectableCss;
    tabs?: InjectableCss;
    successMessage?: InjectableCss;
    okButton?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: InjectableCss;
    inputText?: InjectableCss;
    primaryGreyButton?: InjectableCss;
    validationErrorMessage?: InjectableCss;
}

export const adiNewCreditCardModalRecapStyles: ADINewCreditCardModalRecapStyles = {
    container: {
        css: css`
            display: flex;
            padding: 2px;
            border-radius: 6px;
            border: solid 1px ${ADIColors.gray2};
            background: ${ADIColors.white};
            max-width: 400px;
            width: 100% !important;
        `,
    },
    tabs: {
        css: css`
            padding: 7px 30px;
            border-radius: 6px;
            border: 0;
            background-color: ${ADIColors.white};
            text-align: center;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            line-height: 22px;
            font-weight: normal;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            transition: all 0.1s ease;
            position: relative;
            overflow: visible;
            flex: 1;

            &:nth-of-type(2) {
                margin-left: 10px;
            }

            &:last-child::after {
                display: none;
            }

            &.active {
                background-color: ${ADIColors.darkBlue1};
                color: ${ADIColors.white};
                font-weight: 600;
            }
        `,
    },
    newListContainer: {
        css: css`
            max-width: 370px !important;
            margin-top: -30px;
            > ${GridOffset} {
                > ${GridItemStyle} {
                    display: flex;
                    flex-direction: column;
                    padding-top: 0px;
                    padding-bottom: 0px;
                    margin-bottom: 1rem;
                    > ${TypographyStyle} {
                        margin-bottom: 2px;
                    }
                }
            }
        `,
    },
    labelText: {
        css: css`
            min-height: 19px;
            font-weight: 800;
            line-height: normal;
            color: ${getColor("text.main")};
        `,
    },
    cardTypeRadioGroup: {
        css: css`
            flex-direction: row;
            justify-content: center;
            margin-bottom: 0;
            legend {
                font-size: ${getProp("theme.typography.headerSecondary.size")};
                font-weight: bold;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        justify-content: start;
                        gap: 8px;
                    `,
                    css`
                        flex-direction: column;
                        justify-content: start;
                        gap: 8px;
                    `,
                    css`
                        flex-direction: row;
                        justify-content: center;
                    `,
                    css`
                        flex-direction: row;
                        justify-content: center;
                    `,
                    css`
                        flex-direction: row;
                        justify-content: center;
                    `,
                ])}
            > ${RadioStyle} {
                margin-top: 0px !important;
                label {
                    font-size: ${getProp("theme.typography.h6.size")};
                    font-weight: normal;
                }
            }
        `,
    },
    editInputWapper: {
        css: css`
            display: flex;
            margin-bottom: 3px;
            width: 100%;
            gap: 15px;
        `,
    },
    errorGrid: {
        css: css`
            ${FormFieldStyle} {
                ${TypographyStyle} {
                    color: ${getColor("danger")};
                }
            }
            ${RadioGroupStyle} {
                legend {
                    color: ${getColor("danger")} !important;
                }
            }
        `,
    },
    expError: {
        css: css`
            color: ${getColor("danger")} !important;
        `,
    },
    headingError: {
        css: css`
            color: ${getColor("danger")};
        `,
    },
    continueButton: {
        css: css`
            border-radius: 4px;
            width: 160px;
        `,
    },
    buttonGridItem: {
        css: css`
            flex-direction: row !important;
            justify-content: end;
            gap: 10px;
            margin-bottom: 15px !important;
            width: 100% !important;
            padding: 0px;
        `,
    },
    primaryButton: {
        css: css`
            border-radius: 4px;
            flex: 1;
        `,
    },
    secondaryButton: {
        css: css`
            border-radius: 4px;
            flex: 1;
        `,
    },
    primaryGreyButton: {
        css: css`
            border-radius: 4px;
            flex: 1;
            opacity: 0.5;
            cursor: default;
            outline: none;
            border-color: ${ADIColors.primaryDisabledColor} !important;
            background-color: ${ADIColors.primaryDisabledColor};
            &:hover {
                background-color: ${ADIColors.primaryDisabledColor} !important;
            }
            &:focus {
                outline-color: unset;
            }
        `,
    },
    successMessage: {
        css: css`
            display: inline-flex;
            width: 100%;
            height: 54px;
            margin: 10px 12px 16px 0px;
            font-size: 14px;
            font-weight: 800;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.29;
            letter-spacing: normal;
            color: ${ADIColors.black};
        `,
    },
    okButton: {
        css: css`
            border-radius: 4px;
            width: 100%;
            margin-bottom: 15px;
        `,
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            padding: 30px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    inputText: {
        css: css`
            padding: 10px !important;
        `,
    },
    validationErrorMessage: {
        css: css`
            font-size: 14px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            letter-spacing: normal;
            color: ${ADIColors.black} !important;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            gap: 8px;
        `,
    },
};

const mapStateToProps = (state: ApplicationState) => {
    const session = state.context.session;
    return {
        countries: getCurrentCountries(state),
        session: state.context.session,
    };
};

// new Interface --- P21 Changes
interface customerData {
    email: string;
    customerName: string;
    accountDescription: string;
    accountType: string;
    bankAccountNumber: string;
    routineNumber: string;
    termsAgreed: string;
}

interface OwnProps {
    isFormError: boolean;
    isApiError: boolean;
    openModal: boolean;
    closeModal: Function;
    initiatedByUserName: string;
    doneAddBankAccount: (result: boolean, customerData?: customerData | undefined) => void;
    email?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ThemeProps<BaseTheme>;

const styles = adiNewCreditCardModalRecapStyles;

const TabSectionWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${ADIColors.white};
    flex-wrap: wrap;
    width: 100% !important;
    margin-top: 5px;
`;

const SuccessMessage = styled.div`
    padding: 2px;
    display: flex;
    margin: 5px 0 7px;
    gap: 15px;
    margin-bottom: 15px;
    margin-top: 15px;
    align-items: center;
`;
const ADINewCreditCardModalRecap = ({
    closeModal,
    openModal,
    session,
    isFormError,
    initiatedByUserName,
    doneAddBankAccount,
    isApiError,
}: Props) => {
    const region = window.location.hostname.split(".").pop(); // region ----P21 Change

    const [customerNameLength, setCustomerNameLength] = useState<number>(30);
    const [accountDescriptionLength, setAccountDescriptionLength] = useState<number>(35);
    const [routineNumberLength, setRoutineNumberLength] = useState<number>(9);
    const [accountNumberLength, setAccountNumberLength] = useState<number>(17);
    const [emailLength, setEmailLength] = useState<number>(100);
    const [isLoading, setIsLoading] = useState(false);
    const [isApiErrorMessage, setIsApiErrorMessage] = useState("");
    const [current, setCurrent] = React.useState(0);
    const [isSuccess, setIsSuccess] = useState(false);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const initialYourEmail = isAuthenticated ? session.email || "" : "";
    const [email, setEmail] = useState(initialYourEmail);
    const [customerName, setCustomerName] = useState("");
    const [accountDescription, setAccountDescription] = useState("");
    const [accountType, setAccountType] = useState("CA");
    const [bankAccountNumber, setBankAccountNumber] = useState("");
    const [routineNumber, setRoutineNumber] = useState("");
    const [isError, setIsError] = useState(true);
    const [hostName, setHostName] = useState(window.location.hostname.toString());
    const [validationError, setvalidationError] = useState<boolean>(false);
    const [showErrorMsg, setShowErrorMsg] = useState(false);

    const updateTab = (num: number = 0) => {
        if (current === num) {
            return;
        }
        setCurrent(num);
        if (num === 0) {
            setAccountType("CA");
        }
        if (num === 1) {
            setAccountType("SA");
        }
    };

    useEffect(() => {
        setEmail(session.email);
        setCustomerName("");
        setAccountDescription("");
        setAccountType("CA");
        setBankAccountNumber("");
        setRoutineNumber("");
        setIsSuccess(false);
        setIsApiErrorMessage("");
        setvalidationError(false);
        setIsLoading(false);
    }, [openModal]);

    useEffect(() => {
        isValidation();
    }, [email, customerName, accountDescription, accountType, bankAccountNumber, routineNumber]);

    const handleCustomerName = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget.value.length <= customerNameLength) {
            setCustomerName(e.currentTarget.value);
        }
    };
    const handleAccountDescription = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget.value.length <= accountDescriptionLength) {
            setAccountDescription(e.currentTarget.value);
        }
    };
    const handleBankAccountNumber = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget.value.length <= accountNumberLength) {
            setBankAccountNumber(e.currentTarget.value);
        }
    };
    const handleRoutineNumber = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget.value.length <= routineNumberLength) {
            setRoutineNumber(e.currentTarget.value);
        }
    };
    const handleEmail = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget.value.length <= emailLength) {
            setEmail(e.currentTarget.value);
        }
    };

    // Validation addde -- P21 Change
    const isValidation = (): boolean => {
        let isErrorFlag = false;
        if (
            email === "" ||
            customerName === "" ||
            accountDescription === "" ||
            accountType === "" ||
            bankAccountNumber === "" ||
            routineNumber === ""
        ) {
            setIsError(true);
            isErrorFlag = true;
        } else {
            setIsError(false);
            isErrorFlag = false;
        }
        return isErrorFlag;
    };
    const handleAddressModal = () => {
        if (isValidation()) {
            return;
        }
        if (region === "CA") {
            doneAddBankAccount(true, {
                email,
                customerName,
                accountDescription,
                accountType,
                bankAccountNumber,
                routineNumber,
                termsAgreed: "true",
            });
        } else {
            addAccount();
        }
    };
    const addAccount = async () => {
        if (isValidation()) {
            return;
        }

        const pattern = /^[0-9]+$/;

        const match_routineNumber = routineNumber.match(pattern);
        const match_bankAccountNumber = bankAccountNumber.match(pattern);

        setIsLoading(true);
        try {
            const payload = {
                email,
                customerName,
                accountDescription,
                accountType,
                bankAccountNumber,
                routineNumber,
                termsAgreed: "true",
            };

            if (match_routineNumber && match_bankAccountNumber) {
                const result = await addBankAccount(payload);
                if (result?.result?.returnCode === "01") {
                    setIsSuccess(true);
                    doneAddBankAccount(true);

                    setIsApiErrorMessage("");
                } else {
                    setIsApiErrorMessage(result?.result?.returnMessage);
                }
            } else {
                setvalidationError(true);
            }
        } catch (error) {
            throw error;
        }

        setIsLoading(false);
    };
    return (
        <CustomModelRecap
            headline={translate("Add a New Account")}
            subheading={isSuccess ? "" : translate("* Required Fields")}
            isOpen={openModal}
            errorStyle={isFormError ? true : false}
            handleClose={closeModal}
        >
            <>
                {isApiErrorMessage && (
                    <Typography style={{ color: "red", width: "356px", display: "block" }} size={14}>
                        {isApiErrorMessage}
                    </Typography>
                )}

                {!isSuccess && (
                    <GridContainer {...styles.newListContainer}>
                        <GridItem width={12}>
                            {isFormError && (
                                <Typography color="danger" size={14}>
                                    {translate("* Required Fields")}
                                </Typography>
                            )}
                        </GridItem>
                        <GridItem width={12}>
                            {validationError && (
                                <>
                                    <Typography variant="headerSecondary" {...styles.labelText} size={14}>
                                        {" "}
                                        <ErrorCrossIcon /> &nbsp;
                                        {translate("  Please enter Valid Bank Account information")}
                                    </Typography>
                                </>
                            )}
                        </GridItem>
                        <GridItem width={12}>
                            <Typography variant="headerSecondary" {...styles.labelText}>
                                {translate("Account Type")}
                            </Typography>
                            <TabSectionWrapper>
                                <StyledWrapper {...styles.container}>
                                    <StyledWrapper
                                        {...styles.tabs}
                                        className={current === 0 ? "active" : "normal"}
                                        onClick={() => updateTab(0)}
                                    >
                                        {translate("Checking")}
                                    </StyledWrapper>
                                    <StyledWrapper
                                        {...styles.tabs}
                                        className={current === 1 ? "active" : "normal"}
                                        onClick={() => updateTab(1)}
                                    >
                                        {translate("Savings")}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </TabSectionWrapper>
                        </GridItem>
                        <GridItem width={12}>
                            <Typography variant="headerSecondary" {...styles.labelText}>
                                {translate("Customer Name *")}
                            </Typography>
                            <TextField {...styles.inputText} value={customerName} onChange={handleCustomerName} />
                        </GridItem>
                        <GridItem width={12}>
                            <Typography variant="headerSecondary" {...styles.labelText}>
                                {translate("Account Description *")}
                            </Typography>
                            <TextField
                                {...styles.inputText}
                                value={accountDescription}
                                onChange={handleAccountDescription}
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Typography variant="headerSecondary" {...styles.labelText}>
                                {translate("Routing Number *")}
                            </Typography>
                            <TextField
                                {...styles.inputText}
                                value={routineNumber}
                                onChange={handleRoutineNumber}
                                error={routineNumber.match(/^[0-9]+$/) ? false : validationError}
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Typography variant="headerSecondary" {...styles.labelText}>
                                {translate("Account Number *")}
                            </Typography>
                            <TextField
                                {...styles.inputText}
                                value={bankAccountNumber}
                                onChange={handleBankAccountNumber}
                                error={bankAccountNumber.match(/^[0-9]+$/) ? false : validationError}
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Typography variant="headerSecondary" {...styles.labelText}>
                                {translate("Email *")}
                            </Typography>
                            <TextField {...styles.inputText} value={email} onChange={handleEmail} />
                        </GridItem>

                        <GridItem
                            width={12}
                            css={css`
                                align-items: end;
                            `}
                        >
                            {!isLoading && (
                                <GridItem width={12} {...styles.buttonGridItem}>
                                    <Button
                                        onClick={() => closeModal()}
                                        variant="secondary"
                                        {...styles.secondaryButton}
                                    >
                                        {translate("Cancel")}
                                    </Button>
                                    {isError ? (
                                        <Button variant="primary" {...styles.primaryGreyButton}>
                                            {translate("Add Account")}
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={() => handleAddressModal()}
                                            variant="primary"
                                            {...styles.primaryButton}
                                        >
                                            {translate("Add Account")}
                                        </Button>
                                    )}
                                </GridItem>
                            )}
                            {isLoading && (
                                <StyledWrapper {...styles.centeringWrapper}>
                                    <LoadingSpinner {...styles.spinner} />
                                </StyledWrapper>
                            )}
                        </GridItem>
                    </GridContainer>
                )}

                {isSuccess && (
                    <GridContainer {...styles.newListContainer}>
                        <SuccessMessage>
                            <Icon src={ValidationSuccessIcon} />
                            <Typography {...styles.successMessage}>
                                {translate(
                                    "Your banking information has been submitted and is being processed. You will be able to make online payments in 6-7 business days.",
                                )}
                            </Typography>
                        </SuccessMessage>
                        <Button onClick={() => closeModal()} variant="primary" {...styles.okButton}>
                            {translate("Ok")}
                        </Button>
                    </GridContainer>
                )}
            </>
        </CustomModelRecap>
    );
};

export default connect(mapStateToProps)(withTheme(ADINewCreditCardModalRecap));
